<template>
  <div class="manager-company-status-ui__controls">
    <ManagerCompanyStatsDateFilter
      v-model="payload.date"
      :options="MANAGER_COMPANY_STATS_DATE_OPTIONS"
    />
  </div>
  <VTable class="manager-company-stats slim" :table="table" />
  <VPagination v-if="pagination" :pagination="pagination" @on-go-to-page="fetch" />
</template>

<script setup lang="ts">
  // Types
  import type { ITable } from '~/misc/v-table/types';
  import type {
    TManagerUserStatsResponse,
    TManagerUserStats,
    TManagerUserStatsPayload,
  } from '~/modules/manager/company/stats/types';
  import type { TPagination } from '~/misc/resource/types';

  // Services
  import { ManagerCompanyStatsDataClient } from '~/modules/manager/company/stats/services/stats-data-client.service';

  // Config
  import {
    MANAGER_STATS_TABLE_HEADERS,
    MANAGER_COMPANY_STATS_DATE_OPTIONS,
  } from '~/modules/manager/company/stats/config';

  // Composables
  import { useFeatureOpportunityStats } from '~/shared/stats/composables/use-feature-opportunity-stats';

  // Components
  import VTable from '~/misc/v-table';
  import VPagination from '~/misc/v-pagination/VPagination.vue';
  import ManagerCompanyStatsDateFilter from '~/modules/manager/company/stats/date-filter/ManagerCompanyStatsDateFilter.vue';

  const props = defineProps({
    companyId: {
      type: Number,
      required: true,
    },
  });

  const { featureOpportunityStatsTableData, featureOpportunityStatTitles } =
    useFeatureOpportunityStats();
  const data: Ref<TManagerUserStatsResponse | null> = ref<TManagerUserStatsResponse | null>(null);

  const payload: Ref<TManagerUserStatsPayload> = ref<TManagerUserStatsPayload>({
    date: MANAGER_COMPANY_STATS_DATE_OPTIONS[0].value,
  });

  const pagination: ComputedRef<TPagination | null> = computed<TPagination | null>(
    () => data.value?.meta || null
  );

  const dataClient: ManagerCompanyStatsDataClient = new ManagerCompanyStatsDataClient();

  const userStats: ComputedRef<TManagerUserStats[]> = computed<TManagerUserStats[]>(
    () => data.value?.data ?? []
  );

  const table: ComputedRef<ITable> = computed<ITable>(() => ({
    headers: MANAGER_STATS_TABLE_HEADERS.concat(featureOpportunityStatTitles()),
    data: userStats.value.map((item) => [
      item.name,
      ...featureOpportunityStatsTableData(item.stats),
    ]),
  }));

  const fetch = (page: number = 1) => {
    dataClient
      .request<TManagerUserStatsResponse>('stats', props.companyId, { page, ...payload.value })
      .then((response: TManagerUserStatsResponse) => {
        data.value = response;
      });
  };

  watch(payload.value, () => fetch(), { immediate: true });
</script>
